import type { Building } from "building-types/building/buildingTypes";
import type { GeneratedCeiling } from "building-types/ceilings/ceiling";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { Table } from "common/data/table";
import { resolveOptionsInFormula } from "doc/formula/evalFormulas";
import { getCeilingsForLevel } from "../generationTools/generateCeilingsTools";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  mirror,
  mirrorTranslationX,
  doc: { ceilings, optionSets, instanceOptionSets },
  optionChoices,
  rooms,
  blocks,
  roofSystems: roofs,
  roomsByLevel,
  roomFloorAssemblies,
  interiorPalette,
  exteriorPalette,
  levels,
  levelsAttributes,
  datumAbstractions,
  roomOutlineTable,
  walls,
  entityDisabledByGroup,
}: Pick<
  Building,
  | "mirror"
  | "mirrorTranslationX"
  | "doc"
  | "optionChoices"
  | "rooms"
  | "blocks"
  | "roofSystems"
  | "roomsByLevel"
  | "roomFloorAssemblies"
  | "interiorPalette"
  | "exteriorPalette"
  | "levels"
  | "levelsAttributes"
  | "datumAbstractions"
  | "roomOutlineTable"
  | "walls"
  | "entityDisabledByGroup"
>) {
  const definitions = { optionSets, instanceOptionSets };
  return {
    mirror,
    mirrorTranslationX,
    levels,
    levelsAttributes,
    datumAbstractions,
    blocks,
    rooms,
    optionChoices: resolveOptionsInFormula(ceilings, definitions, optionChoices),
    roofs,
    roomsByLevel,
    roomFloorAssemblies,
    interiorPalette,
    exteriorPalette,
    ceilings,
    roomOutlineTable,
    walls,
    entityDisabledByGroup,
  };
}

const compare = makeGenerationStageComparator<typeof select>({
  optionChoices: "shallow",
  datumAbstractions: "shallow",
  levelsAttributes: "shallow",
});

function generate({
  blocks,
  ceilings: docCeilings,
  rooms,
  levels,
  levelsAttributes,
  datumAbstractions,
  roomOutlineTable,
  roomFloorAssemblies,
  roofs,
  roomsByLevel,
  interiorPalette,
  exteriorPalette,
  walls,
  mirror,
  mirrorTranslationX,
  optionChoices,
  entityDisabledByGroup,
}: ReturnType<typeof select>): {
  ceilings: Table<GeneratedCeiling>;
  ceilingsByLevel: Building["ceilingsByLevel"];
  issues: BuildingGenerationIssue[];
} {
  const ceilings: { [id: string]: GeneratedCeiling } = {};
  const ceilingsByLevel: Building["ceilingsByLevel"] = {};
  const issues: BuildingGenerationIssue[] = [];

  for (const levelId in levels) {
    getCeilingsForLevel(
      levelId,
      levels,
      levelsAttributes,
      datumAbstractions,
      roofs,
      rooms,
      roomFloorAssemblies,
      docCeilings,
      roomsByLevel,
      blocks,
      interiorPalette,
      exteriorPalette,
      roomOutlineTable,
      ceilingsByLevel,
      ceilings,
      walls,
      mirror,
      mirrorTranslationX,
      optionChoices,
      entityDisabledByGroup,
      issues,
    );
  }

  return { ceilings, ceilingsByLevel, issues };
}

export default memo(select, generate, compare);
